<!-- `heightMode` decides who owns the height. Default `"css"` = the consumer's wrapper class does,
     exactly as before. `"fill"` = `fillAvailableHeight` measures the space left below this element and
     writes a pixel height, so no screen needs a `heightCalc-N` guess. `"content"` = ag-grid sizes to
     its rows, which needs the flex-fill rules below to stand down (see `--height-content`). -->
<!-- No `surface-secondary` here. It was tried, to tint the pinned totals row from one place, and
     it CANNOT reach it: every surface below this element - header, viewport, rows, cells - paints its
     own opaque background from the ag-grid theme. The one thing it did tint was the toolbar strip,
     which has no background of its own and sits directly above the column headers, so it read as a
     grey header row. The totals row is tinted where it actually lives instead: the
     `pb-total-pinned-row` class, applied in `fnBuildRowClassRules` and styled in `_ag-grid.scss`. -->
<!-- `--row-click-opens` is the hand pointer over the rows, opted into by
     `gridConfigItems.rowClickOpensItem`. It is a class rather than a style binding because the
     pinned totals row has to be excluded, and that is a structural selector - see `_ag-grid.scss`. -->
<div class="grid-container"
    [ngClass]="{ '--height-content': resolvedHeightMode === 'content', '--row-click-opens': gridConfigItems?.rowClickOpensItem }"
    [fillAvailableHeight]="resolvedHeightMode === 'fill'">
    <!-- The toolbar has TWO shapes, and which one renders is the load-bearing detail of this file.
         `isEnhancedToolbar` is true only for a consumer that binds `segmentTabs`, `bulkActions`, or
         `gridConfigItems.showDateFilter`. Everyone else takes the `@else` branch, which is the
         markup this component has always rendered — same elements, same classes, same nesting — so
         adding the enhanced toolbar cannot move anything on a screen that did not ask for it.
         `--is-enhanced` is what scopes the new flex rules in `_ag-grid.scss` to this mode too.

         The three shared blocks live in `ng-template`s at the bottom so the two shapes cannot drift
         apart; `ng-container` renders no element of its own, so the DOM is unaffected. -->
    @if(showToolbar){
    <div class="grid-container__filter-search" [ngClass]="{ '--is-enhanced': isEnhancedToolbar }">
        @if(isEnhancedToolbar){
        @if(isBulkMode){
        <!-- Caption text belongs to the CALLER: `[bulkSelectedText]` is already-translated text whose
             `{{count}}` token this component substitutes, so a screen holding expenses or reports is
             not stuck saying "Transaction". Unbound, it falls back to the original key pair. See
             `bulkSelectedLabel`. -->
        <div class="grid-container__left">
            <p class="grid-container__bulk-text" id="bulkOrText">{{ bulkSelectedLabel }}</p>

            <!-- Optional value for the bulk action: same control as the filter, but it stages a value
                 instead of changing the rows. Only one of the two is ever on screen. -->
            @if(bulkSelect){
            <div class="grid-container__filter">
                <div class="eod-input">
                    @if(bulkSelect.label){<label class="form-label">{{ bulkSelect.label }}</label>}
                    <ng-select [items]="bulkSelect.options"
                        [bindLabel]="bulkSelect.bindLabel || 'value'"
                        [bindValue]="bulkSelect.bindValue || 'id'"
                        [ngModel]="bulkSelectValue" (ngModelChange)="onBulkSelectValueChange($event)"
                        [placeholder]="bulkSelect.placeholder || ''"
                        [clearable]="false" [searchable]="true"
                        class="select-dropdown select-dropdown--md"
                        [notFoundText]="textNotFound" name="bulkSelect">
                    </ng-select>
                    <small class="helper-text"></small>
                </div>
            </div>
            }
        </div>
        } @else if(hasLeftSlot){
        <!-- Tabs and dropdown are independent, not an either/or — a screen declaring both gets
             both, side by side. No descriptor does today, but a priority chain would silently
             drop one the day it does. -->
        <div class="grid-container__left">
            @if(segmentTabs?.length){
            <ds-segment-tabs [tabs]="segmentTabs" [activeValue]="activeSegmentTab"
                (tabSelected)="onSegmentTabSelected($event)">
            </ds-segment-tabs>
            }

            @if(gridConfigItems?.showFilter){
            <div class="grid-container__filter">
                <ng-container *ngTemplateOutlet="gridFilterDropdown"></ng-container>
            </div>
            }

            <!-- Opt-in date filter (UC7 Screen 73). Native input so the calendar needs no library;
                 the grid only reports the change, the host re-fetches. -->
            @if(gridConfigItems?.showDateFilter){
            <div class="grid-container__filter">
                <div class="eod-input">
                    <label for="gridDateFilter" class="form-label">{{gridConfigItems?.dateFilterLabel}}</label>
                    <input id="gridDateFilter" name="gridDateFilter" type="date" class="form-control"
                        [attr.max]="gridConfigItems?.dateFilterMax || null"
                        [ngModel]="dateFilterValue" (ngModelChange)="onDateFilterChange($event)">
                    <small class="helper-text"></small>
                </div>
            </div>
            }
            @if(gridConfigItems?.showTagTextValue && tagTextValue){
            <ng-container *ngTemplateOutlet="gridTagText"></ng-container>
            }
        </div>
        }

        @if(hasRightSlot){
        <div class="grid-container__search">
            @if(isBulkMode){
            @for(action of bulkActions; track action.key){
            <ds-button [buttonInput]="bulkButtonInput(action)" (btnClick)="onBulkActionClick(action.key)">
            </ds-button>
            }
            <ds-button [buttonInput]="deselectAllButtonInput" (btnClick)="onBulkDeselectAll()">
            </ds-button>
            } @else if(gridConfigItems?.showSearch){
            <ng-container *ngTemplateOutlet="gridSearchBox"></ng-container>
            }
        </div>
        }
        } @else {
        <!-- The original toolbar, unchanged. Do not "tidy" this branch into the one above: its whole
             job is to keep existing consumers' DOM exactly as it was. Note `.grid-container__filter`
             renders even when `showFilter` is false — that empty div is part of the original layout. -->
        <div class="grid-container__filter">
            @if(gridConfigItems?.showFilter){
            <ng-container *ngTemplateOutlet="gridFilterDropdown"></ng-container>
            }
            @if(gridConfigItems?.showTagTextValue && tagTextValue){
            <ng-container *ngTemplateOutlet="gridTagText"></ng-container>
            }
        </div>
        @if(gridConfigItems?.showSearch){
        <div class="grid-container__search">
            <ng-container *ngTemplateOutlet="gridSearchBox"></ng-container>
        </div>
        }
        }
    </div>
    }

    <section class="ag-theme-alpine">
        @if(gridOptions){
        <ag-grid-angular #agGrid class="ag-theme-alpine" style="width: 100%"
            [style.height]="resolvedHeightMode === 'content' ? null : '100%'"
            (gridReady)="onGridReady($event)" (cellClicked)="onCellClicked($event)" [gridOptions]="gridOptions"
            [theme]="'themeAlpine'" [sideBar]="true" [headerHeight]="40" [columnHoverHighlight]="false"
            [pinnedBottomRowData]="pinnedBottomRowData" [rowClassRules]="rowClassRules"
            [overlayNoRowsTemplate]="overlayNoRowsTemplate" [overlayLoadingTemplate]="overlayLoadingTemplate">
        </ag-grid-angular>
        }
    </section>
</div>

<!-- Shared toolbar blocks, referenced from both toolbar shapes above. -->
<ng-template #gridFilterDropdown>
    <div class="eod-input" [ngClass]="gridConfigItems?.searchBarClass">
        <label for="country" class="form-label">{{filterLabel}}</label>
        <ng-select [items]="dropdownList" bindLabel="dropdownName" bindValue="dropdownName"
            [(ngModel)]="selectedFilter" (change)="handleFilterChange($event)" (clear)="onClear()"
            [clearable]="false" [searchable]="true" class="select-dropdown select-dropdown--md"
            [notFoundText]="textNotFound" name="dropdown">
        </ng-select>
        <small class="helper-text"></small>
    </div>
</ng-template>

<ng-template #gridTagText>
    <div class="grid-header_tag">
        <p>{{tagTextValue}}</p>
    </div>
</ng-template>

<ng-template #gridSearchBox>
    <ds-textbox #searchField [textboxInput]="searchTextboxConfig" (valueChange)="onGridSearchTextChange($event)">
    </ds-textbox>
</ng-template>
